// usefulness: 3/5
// description: SessionStorage helpers for hero resume state (last step path + eisenboard facets-derived confidence).

export const EISENBOARD_FACETS_KEY = 'eisenboard_facets_v1';
export const LAST_STEP_PATH_KEY = 'iw_last_step_path';

const STEP_ROUTE_SET = new Set<string>([
  '/crucible',
  '/tracker',
  '/input-loop',
  '/eisenboard',
]);

type StoredFacet = {
  domain?: unknown;
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

export const isStepRoute = (path: string): boolean => STEP_ROUTE_SET.has(path);

export const saveLastStepPath = (path: string): void => {
  if (typeof window === 'undefined') return;
  if (!isStepRoute(path)) return;
  try {
    window.sessionStorage.setItem(LAST_STEP_PATH_KEY, path);
  } catch {
    // ignore storage errors
  }
};

export const readLastStepPath = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.sessionStorage.getItem(LAST_STEP_PATH_KEY);
    if (!value) return null;
    return isStepRoute(value) ? value : null;
  } catch {
    return null;
  }
};

export const readStoredFacets = (): StoredFacet[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(EISENBOARD_FACETS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const computeConfidenceFromStoredFacets = (facets: StoredFacet[]): number => {
  if (!facets || facets.length === 0) return 0;

  const total = facets.length;
  const uniqueDomains = new Set(
    facets
      .map((f) => (typeof f?.domain === 'string' ? f.domain.toUpperCase() : ''))
      .filter(Boolean)
  ).size;
  const domainCoverage = clamp(uniqueDomains / 5, 0, 1);

  const n90 = 11.6;
  const k = Math.log(10) / n90;
  const base = 100 * (1 - Math.exp(-k * total));
  const diversity = 0.6 + 0.4 * domainCoverage;

  return clamp(Math.round(base * diversity), 0, 100);
};

export type HeroSessionState = {
  hasResume: boolean;
  resumePath: string | null;
  confidence: number;
  trackerUnlocked: boolean;
  facetCount: number;
  lastStepPath: string | null;
};

export const readHeroSessionState = (): HeroSessionState => {
  const facets = readStoredFacets();
  const confidence = computeConfidenceFromStoredFacets(facets);
  const trackerUnlocked = confidence >= 90;
  const lastStepPath = readLastStepPath();

  const resumePath = (() => {
    if (lastStepPath) return lastStepPath;
    if (facets.length > 0) return '/crucible';
    return null;
  })();

  return {
    hasResume: Boolean(resumePath),
    resumePath,
    confidence,
    trackerUnlocked,
    facetCount: facets.length,
    lastStepPath,
  };
};

export const isTimeoutErrorText = (text: string): boolean => {
  const normalized = String(text || '').toUpperCase();
  return (
    normalized.includes('ERROR TIMEOUT') ||
    normalized.includes('ERROR 408') ||
    normalized.includes('ERROR 504')
  );
};
